import {
  type FeedSort,
  POST_BODY_MAX,
  POST_IMAGE_BYTES_MAX,
  POST_IMAGE_TYPES,
  POST_IMAGES_MAX,
  POST_TAG_NAMES,
  POST_TAGS,
  type PostTag,
} from '@mogzo/shared';
import { MoreHorizontal, UserPlus } from 'lucide-react';
import { type FormEvent, type Ref, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { Avatar, AvatarStack, Banner, CommunityIcon } from '../../components/community/Avatar';
import { PostCard } from '../../components/community/PostCard';
import { CommunityShell, NoticeCard } from '../../components/community/Shell';
import { useLauncher } from '../../components/play/launch';
import { PlayIcon } from '../../components/play/SplashCard';
import { Alert, buttonClass, PageSpinner } from '../../components/ui';
import { ApiError } from '../../lib/api';
import {
  answerRequest,
  type CommunityPageData as Community,
  createInvite,
  createPost,
  fetchMembers,
  fetchPosts,
  fetchTeamCommunity,
  fetchTestCommunity,
  leaveCommunity,
  type MembersTab,
  type Post,
  type PostImage,
  removeMember,
  removePendingImage,
  setManager,
  timeAgo,
  turnOffInvite,
  uploadPostImage,
} from '../../lib/communities';
import { useSession } from '../../lib/session';

export const SORTS: { value: FeedSort; label: string }[] = [
  { value: 'hot', label: 'Hot' },
  { value: 'new', label: 'New' },
  { value: 'top', label: 'Most GG’d' },
];

const TABS = [
  { value: 'posts', label: 'Posts' },
  { value: 'about', label: 'About' },
  { value: 'members', label: 'Members' },
] as const;
type Tab = (typeof TABS)[number]['value'];

export function chipClass(pressed: boolean, size: 'sm' | 'md' = 'sm') {
  return `rounded-full border font-bold ${size === 'sm' ? 'min-h-[34px] px-3 text-[13px]' : 'min-h-[38px] px-3.5 text-sm'} ${pressed ? 'border-ink bg-ink text-white' : 'border-line bg-card text-ink hover:border-muted'}`;
}

type State =
  | { status: 'loading' }
  | { status: 'private' }
  | { status: 'failed' }
  | { status: 'ready'; data: Community };

/** Shown to anyone who isn't in the community, and for addresses that don't exist. */
export function PrivateTest({ next, kind = 'test' }: { next: string; kind?: 'test' | 'team' }) {
  const { me } = useSession();
  return (
    <NoticeCard title={kind === 'team' ? 'This community is private' : 'This test is private'}>
      <p>
        {kind === 'team'
          ? 'Only its members can see it. Got an invite link? Open it to ask to join.'
          : 'Only the developer and the testers they invite can see it. Got an invite link? Open it to ask to join.'}
      </p>
      {!me && (
        <Link
          to={`/sign-in?next=${encodeURIComponent(next)}`}
          className={buttonClass('primary', false)}
        >
          Sign in
        </Link>
      )}
    </NoticeCard>
  );
}

/** mogzo.com/test/<address>: a game's private test community. */
export function TestCommunity() {
  return <CommunityRoute kind="test" load={(slug) => fetchTestCommunity(slug)} />;
}

/** mogzo.com/team/<address>: a private team community staff made (M28). */
export function TeamCommunity() {
  return <CommunityRoute kind="team" load={fetchTeamCommunity} />;
}

function CommunityRoute({
  kind,
  load,
}: {
  kind: 'test' | 'team';
  load: (slug: string) => Promise<Community>;
}) {
  const { slug = '' } = useParams();
  const { me } = useSession();
  const [state, setState] = useState<State>({ status: 'loading' });

  useEffect(() => {
    if (me === undefined) return;
    if (me === null) {
      setState({ status: 'private' });
      return;
    }
    let active = true;
    load(slug)
      .then((data) => active && setState({ status: 'ready', data }))
      .catch((error) => {
        if (!active) return;
        const hidden = error instanceof ApiError && [401, 403, 404].includes(error.status);
        setState({ status: hidden ? 'private' : 'failed' });
      });
    return () => {
      active = false;
    };
  }, [slug, me, load]);

  if (state.status === 'loading') return <PageSpinner />;
  return (
    <CommunityShell title={state.status === 'ready' ? state.data.community.name : undefined}>
      {state.status === 'private' && <PrivateTest kind={kind} next={`/${kind}/${slug}`} />}
      {state.status === 'failed' && (
        <NoticeCard title="Couldn’t load this page">
          <p>MOGZO didn’t answer. Reload the page to try again.</p>
        </NoticeCard>
      )}
      {state.status === 'ready' && <CommunityPage data={state.data} />}
    </CommunityShell>
  );
}

/** The Members tab's data, loaded with the page so the tab can count who's waiting. */
function useMembersTab(communityId: string) {
  const [tab, setTab] = useState<MembersTab | null>(null);
  const [failed, setFailed] = useState(false);
  const reload = useCallback(async () => {
    try {
      setTab(await fetchMembers(communityId));
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, [communityId]);
  useEffect(() => {
    void reload();
  }, [reload]);
  return { tab, failed, reload };
}

function CommunityPage({ data }: { data: Community }) {
  const { kind, community, viewer, build } = data;
  const isDeveloper = kind === 'test' && viewer.role === 'owner';
  const isManager = viewer.role === 'owner' || viewer.role === 'manager';
  const members = useMembersTab(community.id);
  const waiting = members.tab?.requests.length ?? 0;
  const [params, setParams] = useSearchParams();
  const tab: Tab = TABS.some((t) => t.value === params.get('tab'))
    ? (params.get('tab') as Tab)
    : 'posts';
  const [sort, setSort] = useState<FeedSort>('hot');
  const [posts, setPosts] = useState<Post[] | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [feedFailed, setFeedFailed] = useState(false);
  const { launch, layer } = useLauncher({
    slug: community.slug,
    name: community.name,
    projectId: community.projectId ?? '',
    isDeveloper,
  });
  const [composing, setComposing] = useState(false);
  const composer = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    let active = true;
    setPosts(null);
    setFeedFailed(false);
    fetchPosts(community.id, sort)
      .then((page) => {
        if (!active) return;
        setPosts(page.posts);
        setHasMore(page.hasMore);
      })
      .catch(() => active && setFeedFailed(true));
    return () => {
      active = false;
    };
  }, [community.id, sort]);

  async function loadMore() {
    if (!posts) return;
    setLoadingMore(true);
    try {
      const page = await fetchPosts(community.id, sort, posts.length);
      setPosts([...posts, ...page.posts.filter((p) => !posts.some((q) => q.id === p.id))]);
      setHasMore(page.hasMore);
    } catch {
      setFeedFailed(true);
    } finally {
      setLoadingMore(false);
    }
  }

  function showTab(next: Tab) {
    setParams(next === 'posts' ? {} : { tab: next }, { replace: true });
  }

  function openInvite() {
    showTab('members');
    requestAnimationFrame(() =>
      document.getElementById('invite')?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
    );
  }

  function openComposer() {
    setComposing(true);
    requestAnimationFrame(() => composer.current?.focus());
  }

  const memberCount = `${community.memberCount} member${community.memberCount === 1 ? '' : 's'}`;
  const kindName = kind === 'team' ? 'Private team' : 'Private test';
  const meta =
    kind === 'team'
      ? [community.about, memberCount].filter(Boolean).join(' · ')
      : [
          `by ${community.developerName}`,
          memberCount,
          build ? `testing build #${build.number}` : 'no build yet',
        ].join(' · ');

  return (
    <>
      <section aria-label="Community" className="border-b border-divider bg-card">
        <Banner className="h-[84px] sm:h-[168px]" />
        <div className="mx-auto w-full max-w-[1180px] px-4 sm:px-6">
          <div className="flex items-start gap-3 sm:flex-wrap sm:gap-x-5 sm:gap-y-3">
            <span className="relative z-10 -mt-6 sm:-mt-10">
              <CommunityIcon name={community.name} />
            </span>
            <div className="min-w-0 flex-1 pt-2.5 sm:flex-[1_1_220px] sm:pt-4">
              <div className="flex flex-wrap items-center gap-2.5">
                <h1 className="text-xl font-bold sm:text-[30px]">{community.name}</h1>
                <span className="hidden items-center gap-1.5 rounded-full border border-[#f3dfb5] bg-cream px-2.5 py-1 text-xs font-bold sm:inline-flex">
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <rect x="5" y="11" width="14" height="10" rx="2" />
                    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                  </svg>
                  {kindName}
                </span>
              </div>
              <p className="mt-1 hidden text-muted sm:block">{meta}</p>
              <p className="text-xs text-muted sm:hidden">
                {kindName} · {memberCount}
              </p>
            </div>
            <div className="flex items-center gap-2.5 pt-2.5 sm:flex-wrap sm:pt-[18px]">
              <span className="hidden sm:block">
                <AvatarStack names={community.members.map((m) => m.name)} />
              </span>
              {isManager && (
                <button
                  type="button"
                  onClick={openInvite}
                  className={`inline-flex min-h-10 items-center gap-2 rounded-full px-4 text-sm font-bold sm:min-h-11 sm:px-[18px] ${build ? 'border border-line hover:border-muted' : 'bg-ink text-white hover:bg-ink-soft'}`}
                >
                  <UserPlus size={16} strokeWidth={2} aria-hidden="true" />
                  Invite
                </button>
              )}
              {build && (
                <button
                  type="button"
                  onClick={() => launch(build)}
                  className="inline-flex min-h-10 items-center gap-2 rounded-full bg-brand px-4 text-sm font-bold text-ink hover:bg-brand-hover sm:min-h-11 sm:px-[22px] sm:text-[15px]"
                >
                  <PlayIcon size={14} />
                  Play
                </button>
              )}
            </div>
          </div>
          <nav aria-label="Community sections" className="mt-2 flex gap-1 sm:mt-4">
            {TABS.map((t) => (
              <button
                key={t.value}
                type="button"
                aria-current={tab === t.value ? 'page' : undefined}
                onClick={() => showTab(t.value)}
                className={`border-b-[3px] px-3 py-3 sm:px-4 ${tab === t.value ? 'border-ink font-bold' : 'border-transparent font-semibold text-muted hover:text-ink'}`}
              >
                {t.label}
                {t.value === 'members' && waiting > 0 && (
                  <span className="ml-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-brand px-1.5 text-[11px] font-bold text-ink">
                    {waiting}
                    <span className="sr-only"> waiting</span>
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>
      </section>

      <div className="mx-auto flex w-full max-w-[1180px] flex-wrap items-start gap-6 px-3 pt-4 pb-16 sm:px-6 sm:pt-5">
        <main className="flex max-w-[700px] min-w-0 flex-[999_1_560px] flex-col gap-3.5">
          {tab === 'posts' && (
            <>
              <Composer
                ref={composer}
                communityId={community.id}
                viewerName={viewer.name}
                open={composing}
                onPosted={(post) => {
                  setPosts((current) => {
                    const list = current ?? [];
                    const pinned = list.filter((p) => p.pinned);
                    return [...pinned, post, ...list.filter((p) => !p.pinned)];
                  });
                  setComposing(false);
                }}
              />
              <fieldset className="flex flex-wrap gap-2">
                <legend className="sr-only">Sort posts</legend>
                {SORTS.map((s) => (
                  <button
                    key={s.value}
                    type="button"
                    aria-pressed={sort === s.value}
                    onClick={() => setSort(s.value)}
                    className={chipClass(sort === s.value, 'md')}
                  >
                    {s.label}
                  </button>
                ))}
              </fieldset>

              {!build && isDeveloper && (
                <div className="rounded-[18px] border border-line bg-card px-5 py-4">
                  <p className="font-bold">Nothing to play yet</p>
                  <p className="mt-1 text-sm leading-relaxed text-muted">
                    Upload a build with the MOGZO CLI. Each ready build is posted here with the
                    game’s splash, and pinned on top.
                  </p>
                </div>
              )}

              {feedFailed && <Alert>Couldn’t load posts. Reload the page to try again.</Alert>}
              {!posts && !feedFailed && (
                <p role="status" className="py-6 text-center text-sm text-muted">
                  Loading posts…
                </p>
              )}
              {posts?.length === 0 && (
                <p className="rounded-[18px] border border-dashed border-line px-5 py-8 text-center text-muted">
                  No posts yet. Share the first thing you notice.
                </p>
              )}
              {posts?.map((post) => (
                <PostCard key={post.id} post={post} community={community} onLaunch={launch} />
              ))}
              {hasMore && (
                <button
                  type="button"
                  onClick={loadMore}
                  disabled={loadingMore}
                  className={buttonClass('secondary')}
                >
                  {loadingMore ? 'Loading…' : 'Show more posts'}
                </button>
              )}
            </>
          )}
          {tab === 'about' && <AboutCards data={data} />}
          {tab === 'members' && <Members data={data} {...members} />}
        </main>

        {tab !== 'about' && (
          <aside className="hidden min-w-0 flex-[1_1_280px] flex-col gap-3.5 md:flex">
            <AboutCards data={data} />
          </aside>
        )}
      </div>

      {layer}
      {tab === 'posts' && !composing && (
        <button
          type="button"
          aria-label="New post"
          onClick={openComposer}
          className="fixed right-4 bottom-5 flex size-14 items-center justify-center rounded-full bg-ink text-white shadow-lg sm:hidden"
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M12 5v14M5 12h14" />
          </svg>
        </button>
      )}
    </>
  );
}

type Attachment =
  | { key: string; status: 'uploading' }
  | { key: string; status: 'ready'; image: PostImage };

const IMAGE_TYPES: readonly string[] = POST_IMAGE_TYPES;

export function Composer({
  ref,
  communityId,
  viewerName,
  placeholder = 'Share something with the testers…',
  open,
  onPosted,
}: {
  ref: Ref<HTMLTextAreaElement>;
  communityId: string;
  viewerName: string;
  placeholder?: string;
  /** On phones the composer opens from the + button; on wider screens it's always there. */
  open: boolean;
  onPosted: (post: Post) => void;
}) {
  const [body, setBody] = useState('');
  const [tag, setTag] = useState<PostTag | null>(null);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const picker = useRef<HTMLInputElement>(null);
  const uploading = attachments.some((a) => a.status === 'uploading');
  const ready = attachments.flatMap((a) => (a.status === 'ready' ? [a.image] : []));

  /** Uploads straight away, so posting is quick and problems show early. */
  function attach(files: File[]) {
    setError(null);
    const room = POST_IMAGES_MAX - attachments.length;
    if (files.length > room) setError(`Up to ${POST_IMAGES_MAX} pictures per post.`);
    for (const file of files.slice(0, Math.max(room, 0))) {
      if (!IMAGE_TYPES.includes(file.type)) {
        setError('Use PNG, JPEG, WebP or GIF pictures.');
        continue;
      }
      if (file.size > POST_IMAGE_BYTES_MAX) {
        setError(`Pictures can be up to ${POST_IMAGE_BYTES_MAX / 1024 / 1024} MB.`);
        continue;
      }
      const key = crypto.randomUUID();
      setAttachments((current) => [...current, { key, status: 'uploading' }]);
      uploadPostImage(communityId, file)
        .then((image) =>
          setAttachments((current) =>
            current.map((a) => (a.key === key ? { key, status: 'ready', image } : a)),
          ),
        )
        .catch((failed) => {
          setAttachments((current) => current.filter((a) => a.key !== key));
          setError(failed instanceof Error ? failed.message : 'Couldn’t add that picture.');
        });
    }
  }

  function detach(attachment: Attachment) {
    setAttachments((current) => current.filter((a) => a.key !== attachment.key));
    if (attachment.status === 'ready') {
      removePendingImage(attachment.image.id).catch(() => undefined);
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if ((!body.trim() && !ready.length) || uploading) return;
    setBusy(true);
    setError(null);
    try {
      const post = await createPost(communityId, {
        body: body.trim(),
        tag,
        imageIds: ready.map((image) => image.id),
      });
      setBody('');
      setTag(null);
      setAttachments([]);
      onPosted(post);
    } catch (failed) {
      setError(failed instanceof Error ? failed.message : 'Couldn’t post. Try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      aria-label="New post"
      onSubmit={submit}
      className={`${open ? 'flex' : 'hidden sm:flex'} flex-col gap-2.5 rounded-[18px] border border-line bg-card px-4 py-3.5`}
    >
      {error && <Alert>{error}</Alert>}
      <div className="flex items-start gap-3">
        <Avatar name={viewerName} />
        <label htmlFor="compose" className="sr-only">
          New post
        </label>
        <textarea
          id="compose"
          ref={ref}
          rows={2}
          maxLength={POST_BODY_MAX}
          placeholder={placeholder}
          value={body}
          onChange={(event) => setBody(event.target.value)}
          onPaste={(event) => {
            const files = [...event.clipboardData.files].filter((f) => f.type.startsWith('image/'));
            if (files.length) {
              event.preventDefault();
              attach(files);
            }
          }}
          className="min-w-0 flex-1 resize-y rounded-[14px] border border-line bg-field px-3.5 py-2.5 text-base text-ink placeholder:text-subtle focus:border-ink focus:bg-card focus:outline-none"
        />
      </div>
      {attachments.length > 0 && (
        <ul aria-label="Pictures to post" className="flex flex-wrap gap-2 sm:pl-[52px]">
          {attachments.map((attachment, i) => (
            <li key={attachment.key} className="relative size-20">
              {attachment.status === 'ready' ? (
                <img
                  src={attachment.image.thumbUrl}
                  alt={`Attachment ${i + 1}`}
                  className="size-20 rounded-xl bg-field object-cover"
                />
              ) : (
                <span
                  role="status"
                  className="flex size-20 items-center justify-center rounded-xl bg-field text-xs font-semibold text-muted"
                >
                  Adding…
                </span>
              )}
              <button
                type="button"
                aria-label={`Remove picture ${i + 1}`}
                onClick={() => detach(attachment)}
                className="absolute -top-2 -right-2 flex size-7 items-center justify-center rounded-full border-2 border-card bg-ink text-white"
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2.5 sm:pl-[52px]">
        <div className="flex flex-wrap items-center gap-1.5">
          <input
            ref={picker}
            type="file"
            accept={POST_IMAGE_TYPES.join(',')}
            multiple
            hidden
            aria-label="Choose pictures"
            onChange={(event) => {
              attach([...(event.target.files ?? [])]);
              event.target.value = '';
            }}
          />
          <button
            type="button"
            onClick={() => picker.current?.click()}
            disabled={attachments.length >= POST_IMAGES_MAX}
            className={`${chipClass(false)} inline-flex items-center gap-1.5 disabled:opacity-50`}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <rect x="3" y="5" width="18" height="14" rx="2" />
              <circle cx="9" cy="10" r="1.5" />
              <path d="M21 16l-5-5-8 8" />
            </svg>
            Picture
          </button>
          <fieldset className="flex flex-wrap gap-1.5">
            <legend className="sr-only">Tag (optional)</legend>
            {POST_TAGS.map((t) => (
              <button
                key={t}
                type="button"
                aria-pressed={tag === t}
                onClick={() => setTag(tag === t ? null : t)}
                className={chipClass(tag === t)}
              >
                {POST_TAG_NAMES[t]}
              </button>
            ))}
          </fieldset>
        </div>
        <button
          type="submit"
          disabled={busy || uploading || (!body.trim() && !ready.length)}
          className={buttonClass('primary', false)}
        >
          {busy ? 'Posting…' : uploading ? 'Adding pictures…' : 'Post'}
        </button>
      </div>
    </form>
  );
}

function AboutCards({ data }: { data: Community }) {
  const { community, build, viewer } = data;
  const navigate = useNavigate();
  const [leaveError, setLeaveError] = useState<string | null>(null);

  const isTeam = data.kind === 'team';

  async function leave() {
    const what = isTeam ? community.name : `the ${community.name} test`;
    if (!window.confirm(`Leave ${what}? You’ll need a new invite to come back.`)) return;
    try {
      await leaveCommunity(community.id);
      navigate('/account', { replace: true });
    } catch (failed) {
      setLeaveError(failed instanceof Error ? failed.message : 'Couldn’t leave. Try again.');
    }
  }
  const started = new Date(community.createdAt).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  return (
    <>
      <section
        aria-labelledby="about-heading"
        className="rounded-[18px] border border-line bg-card px-5 py-[18px]"
      >
        <h2 id="about-heading" className="text-[17px] font-bold">
          {isTeam ? 'About' : 'About this test'}
        </h2>
        <p className="mt-2 mb-3 text-sm leading-relaxed text-muted">
          {isTeam
            ? community.about ||
              'A private team community. Only members see it, its posts and who’s in it.'
            : `${community.developerName}’s private test of ${community.name}. Play the newest build, post what you find, and GG the posts you agree with.`}
        </p>
        <dl className="text-sm">
          {[
            ...(isTeam
              ? []
              : [
                  [
                    'Newest build',
                    build ? `#${build.number} · ${timeAgo(build.createdAt)}` : 'None yet',
                  ],
                ]),
            ['Members', String(community.memberCount)],
            ['Started', started],
          ].map(([term, value]) => (
            <div key={term} className="flex justify-between gap-3 border-t border-divider py-2.5">
              <dt className="text-muted">{term}</dt>
              <dd className="font-bold">{value}</dd>
            </div>
          ))}
        </dl>
        {viewer.role !== 'owner' && (
          <>
            {leaveError && <Alert>{leaveError}</Alert>}
            <button
              type="button"
              onClick={leave}
              className="mt-1 text-sm font-semibold text-muted underline hover:text-ink"
            >
              {isTeam ? 'Leave this community' : 'Leave this test'}
            </button>
          </>
        )}
      </section>
      <section
        aria-labelledby="rules-heading"
        className="rounded-[18px] border border-line bg-card px-5 py-[18px]"
      >
        <h2 id="rules-heading" className="text-[17px] font-bold">
          Before you post
        </h2>
        <ol className="mt-2.5 list-decimal space-y-0.5 pl-5 text-sm leading-relaxed text-muted">
          <li>
            {isTeam
              ? 'Keep it private: what’s posted here stays here.'
              : 'Keep it private: don’t share the game outside this test.'}
          </li>
          <li>Be specific and kind.</li>
          <li>
            MOGZO’s{' '}
            <Link to="/terms" className="font-semibold text-ink underline">
              Terms
            </Link>{' '}
            apply; managers can remove posts.
          </li>
        </ol>
      </section>
    </>
  );
}

const ROLE_NAMES = { owner: 'Manager', manager: 'Manager', tester: 'Tester', member: 'Member' };

/** A new link works for a week, for up to ten people; managers can turn it off sooner. */
const NEW_LINK = { days: 7, maxUses: 10 } as const;

const linkDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

/**
 * The Members tab (M28). Managers see who's waiting to join (opened an invite link), the
 * community's invite link, and a menu on each member; everyone sees who's in it.
 */
function Members({
  data,
  tab,
  failed,
  reload,
}: {
  data: Community;
  tab: MembersTab | null;
  failed: boolean;
  reload: () => Promise<void>;
}) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [menu, setMenu] = useState<string | null>(null);
  const { community, kind } = data;

  if (failed) return <Alert>Couldn’t load members. Reload the page to try again.</Alert>;
  if (!tab) {
    return (
      <p role="status" className="py-6 text-center text-sm text-muted">
        Loading members…
      </p>
    );
  }

  async function act(key: string, action: () => Promise<unknown>) {
    setBusy(key);
    setError(null);
    setMenu(null);
    try {
      await action();
      await reload();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'That didn’t work. Try again.');
    } finally {
      setBusy(null);
    }
  }

  async function copy(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Couldn’t copy. Select the link and copy it.');
    }
  }

  const { canManage } = tab.viewer;
  const link = tab.invites[0];
  const memberLine = (member: MembersTab['members'][number]) => {
    const handle = member.username ? `@${member.username} · ` : '';
    if (member.role === 'owner') {
      return `${handle}${kind === 'team' ? 'made this community' : 'the developer'}`;
    }
    return `${handle}joined ${linkDate(member.joinedAt)}`;
  };

  return (
    <>
      {error && <Alert>{error}</Alert>}

      {canManage && tab.requests.length > 0 && (
        <section
          aria-labelledby="waiting-heading"
          className="rounded-[18px] border border-[#f3dfb5] bg-cream px-4 pt-3.5 pb-1.5 sm:px-5 sm:pt-[18px]"
        >
          <h2 id="waiting-heading" className="text-[17px] font-bold">
            Waiting to join
          </h2>
          <p className="mt-0.5 mb-1.5 text-sm text-muted">
            They opened an invite link. Accept to let them in.
          </p>
          <ul>
            {tab.requests.map((request) => (
              <li
                key={request.userId}
                className="flex items-center gap-3 border-t border-[#f3dfb5] py-3"
              >
                <Avatar name={request.name} />
                <span className="min-w-0 flex-1 leading-[19px]">
                  <b className="block truncate font-semibold">{request.name}</b>
                  <span className="text-[13px] text-muted">
                    {request.username ? `@${request.username} · ` : ''}
                    {timeAgo(request.requestedAt)}
                  </span>
                </span>
                <button
                  type="button"
                  aria-label={`Accept ${request.name}`}
                  disabled={busy !== null}
                  onClick={() =>
                    act(request.userId, () => answerRequest(community.id, request.userId, 'accept'))
                  }
                  className="inline-flex h-8 items-center rounded-full bg-ink px-3 text-[13px] font-semibold text-white hover:bg-ink-soft disabled:opacity-60"
                >
                  Accept
                </button>
                <button
                  type="button"
                  aria-label={`Decline ${request.name}`}
                  disabled={busy !== null}
                  onClick={() =>
                    act(request.userId, () =>
                      answerRequest(community.id, request.userId, 'decline'),
                    )
                  }
                  className="inline-flex h-8 items-center rounded-full border border-[#ead19a] px-3 text-[13px] font-semibold hover:border-muted disabled:opacity-60"
                >
                  Decline
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {canManage && (
        <section
          id="invite"
          aria-labelledby="invite-heading"
          className="scroll-mt-20 rounded-[18px] border border-line bg-card px-4 py-3.5 sm:px-5 sm:py-[18px]"
        >
          <h2 id="invite-heading" className="text-[17px] font-bold">
            Invite people
          </h2>
          <p className="mt-0.5 text-sm text-muted">
            Send this link to the people you want. Opening it asks to join
            {kind === 'test' ? '; testers must be 18 or older.' : '.'}
          </p>
          {link ? (
            <>
              <div className="mt-3 flex items-center gap-2.5 rounded-[14px] bg-field py-1.5 pr-1.5 pl-4">
                <code className="min-w-0 flex-1 truncate font-mono text-[15px]">
                  {link.url.replace(/^https?:\/\//, '')}
                </code>
                <button
                  type="button"
                  onClick={() => copy(link.url)}
                  className="inline-flex h-9 items-center rounded-full bg-ink px-4 text-sm font-semibold text-white hover:bg-ink-soft"
                >
                  {copied ? 'Copied' : 'Copy link'}
                </button>
              </div>
              <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm text-muted">
                  Works until {linkDate(link.expiresAt)} · {link.uses} of {link.maxUses} people have
                  used it
                </span>
                <span className="flex gap-1">
                  <button
                    type="button"
                    disabled={busy !== null}
                    onClick={() => act('new-link', () => createInvite(community.id, NEW_LINK))}
                    className="inline-flex h-8 items-center rounded-full px-3 text-[13px] font-semibold text-muted hover:text-ink"
                  >
                    New link
                  </button>
                  <button
                    type="button"
                    disabled={busy !== null}
                    onClick={() => act('turn-off', () => turnOffInvite(community.id, link.id))}
                    className="inline-flex h-8 items-center rounded-full px-3 text-[13px] font-semibold text-muted hover:text-ink"
                  >
                    Turn off
                  </button>
                </span>
              </div>
            </>
          ) : (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
              <span className="text-sm text-muted">No link works right now.</span>
              <button
                type="button"
                disabled={busy !== null}
                onClick={() => act('new-link', () => createInvite(community.id, NEW_LINK))}
                className="inline-flex h-9 items-center rounded-full bg-ink px-4 text-sm font-semibold text-white hover:bg-ink-soft disabled:opacity-60"
              >
                Make a link
              </button>
            </div>
          )}
        </section>
      )}

      <section
        aria-labelledby="members-heading"
        className="rounded-[18px] border border-line bg-card px-4 pt-3.5 pb-1.5 sm:px-5 sm:pt-[18px]"
      >
        <h2 id="members-heading" className="text-[17px] font-bold">
          Members <span className="font-medium text-muted">· {tab.members.length}</span>
        </h2>
        <ul className="mt-1.5">
          {tab.members.map((member) => {
            const managed = canManage && member.role !== 'owner' && !member.isYou;
            const isManager = member.role === 'owner' || member.role === 'manager';
            return (
              <li
                key={member.userId}
                className="relative flex min-h-14 items-center gap-3 border-t border-divider py-1.5"
              >
                <Avatar name={member.name} />
                <span className="min-w-0 flex-1 leading-[18px]">
                  <b className="block truncate font-semibold">
                    {member.name}
                    {member.isYou && <span className="font-normal text-muted"> (you)</span>}
                  </b>
                  <span className="text-[13px] text-muted">{memberLine(member)}</span>
                </span>
                <span
                  className={`inline-flex h-6 items-center rounded-full border px-2.5 text-xs font-semibold ${isManager ? 'border-[#f3dfb5] bg-cream' : 'border-line bg-field'}`}
                >
                  {ROLE_NAMES[member.role]}
                </span>
                {managed ? (
                  <button
                    type="button"
                    aria-label={`Actions for ${member.name}`}
                    aria-haspopup="true"
                    aria-expanded={menu === member.userId}
                    onClick={() => setMenu(menu === member.userId ? null : member.userId)}
                    className="flex size-10 items-center justify-center rounded-full hover:bg-field"
                  >
                    <MoreHorizontal size={20} strokeWidth={1.9} aria-hidden="true" />
                  </button>
                ) : (
                  canManage && <span className="w-10" />
                )}
                {managed && menu === member.userId && (
                  <div className="absolute top-full right-0 z-10 flex w-56 flex-col rounded-xl border border-line bg-card py-1.5 shadow-[0_8px_24px_rgba(27,21,47,0.14)]">
                    <button
                      type="button"
                      onClick={() =>
                        act(member.userId, () =>
                          setManager(community.id, member.userId, member.role !== 'manager'),
                        )
                      }
                      className="px-4 py-2.5 text-left text-sm font-semibold hover:bg-field"
                    >
                      {member.role === 'manager' ? 'Stop being a manager' : 'Make manager'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (
                          window.confirm(
                            `Remove ${member.name}? They’ll need a new invite to come back.`,
                          )
                        ) {
                          void act(member.userId, () => removeMember(community.id, member.userId));
                        }
                      }}
                      className="px-4 py-2.5 text-left text-sm font-semibold text-[#c23a32] hover:bg-field"
                    >
                      Remove from community
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </section>
    </>
  );
}
